/**
 * /checkout — legacy subscription-checkout route, now a thin redirector.
 *
 * Subscriptions (Pro/Power/Max monthly) have been retired in favour of a
 * prepaid wallet (sign up → $5 credit → pay-as-you-go, top up anytime).
 * Top-ups happen *inside* the app (Settings → Billing, or the low-balance
 * prompt), never on a marketing route. So this page no longer mints a
 * Stripe Checkout session — it just gets the visitor into the app:
 *
 *   - authed   → replace() to `/` (the app handles balance + top-ups).
 *   - unauthed → replace() to `/login?return=/` so LoginPage replays the
 *     navigation into the app after a successful sign-in.
 *
 * Any inbound `?tier=` query is ignored — there are no tiers anymore.
 * Old marketing links / bookmarks pointing at /checkout?tier=pro still
 * resolve gracefully to this redirect.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { MarketingShell } from './MarketingShell';

interface Props {
  /** If the caller already knows the auth state (e.g. App.tsx routed
   *  here from the authed branch), pass `true` to skip the /api/me probe
   *  and bounce straight into the app. */
  knownAuthed?: boolean;
}

export function CheckoutRedirect({ knownAuthed }: Props) {
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;

    async function run() {
      // Resolve auth: trust knownAuthed when provided, else probe /api/me.
      let authed = knownAuthed;
      if (authed === undefined) {
        try {
          const me = await api.me();
          authed = !!me;
        } catch {
          authed = false;
        }
      }
      if (cancelled) return;

      if (authed) {
        // Already signed in — drop them in the app. Top-ups live there.
        window.location.replace('/');
      } else {
        // Round-trip through login, then return to the app root.
        window.location.replace(`/login?return=${encodeURIComponent('/')}`);
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [knownAuthed]);

  return (
    <MarketingShell
      documentTitle={t('pricing.checkoutRedirectTitle')}
      documentDescription={t('pricing.checkoutRedirectDescription')}
    >
      <section
        className="hero"
        style={{
          paddingTop: 120,
          paddingBottom: 120,
          minHeight: '60vh',
        }}
      >
        <div style={{ maxWidth: 640 }}>
          <span className="label label-amber">{t('pricing.checkoutRedirectLabel')}</span>
          <h1
            className="serif"
            style={{
              fontSize: 'clamp(36px, 4vw, 56px)',
              lineHeight: 1.1,
              margin: '12px 0 16px',
            }}
          >
            {t('pricing.checkoutRedirectHeading')}
          </h1>
          <p className="lede">{t('pricing.checkoutRedirectBody')}</p>
        </div>
      </section>
    </MarketingShell>
  );
}
